import React, { useState, useRef } from 'react';
import { Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';

const KeywordInput = ({
    keywords = [],
    onChange,
    onSearch,
    onInputChange,
    loading = false,
    placeholder = "Enter a topic (e.g., 'Technology', 'Fitness', 'Gaming')...",
    disabled = false
}) => {
    const [inputValue, setInputValue] = useState('');
    const [hasError, setHasError] = useState(false);
    const inputRef = useRef(null);

    // Expose inputValue to parent via callback (only when no error)
    React.useEffect(() => {
        if (hasError) return; // Don't update parent when there's an error

        if (onChange && inputValue.trim()) {
            // Just update parent with current input value (not as a keyword list)
            onChange([inputValue.trim()]);
        } else if (onChange && !inputValue.trim()) {
            onChange([]);
        }
    }, [inputValue, hasError]);

    // Handle input change
    const handleInputChange = (e) => {
        const value = e.target.value;

        // Check for symbols - only allow letters, numbers, and spaces
        // Regex allows: a-z, A-Z, 0-9, and spaces. Everything else is rejected.
        const symbolPattern = /[^a-zA-Z0-9\s]/;

        if (symbolPattern.test(value)) {
            // Show notification that only plain text is accepted
            onSearch('Only plain text is accepted. Please remove any symbols or special characters.', true);
            setHasError(true); // Set error state
            // Keep the symbol in the input - let user remove it
            setInputValue(value);
            return;
        }

        // Clear error when user types/removes symbols and input is now valid
        // This handles both typing new characters AND removing symbols
        if (hasError && !symbolPattern.test(value)) {
            setHasError(false); // Clear error state
            if (onInputChange) {
                onInputChange();
            }
        }

        setInputValue(value);
    };

    // Handle key down events
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            // If there's text in input, trigger search directly
            if (inputValue.trim()) {
                const keyword = inputValue.trim();
                onSearch(keyword);
            }
        }
    };

    return (
        <div className="relative">
            {/* Input Container */}
            <div className="bg-zinc-900/40 backdrop-blur-xl border border-white/10 rounded-2xl p-1.5 flex items-center gap-2 shadow-lg shadow-black/30 min-h-[36px]">
                {/* Search Icon */}
                <div className="pl-2">
                    <MagnifyingGlassIcon className="w-3.5 h-3.5 text-violet-400" />
                </div>

                {/* Text Input */}
                <input
                    ref={inputRef}
                    type="text"
                    className="flex-1 min-w-[120px] bg-transparent border-none h-7 text-sm text-white placeholder:text-zinc-500 focus:ring-0 focus:outline-none"
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                />
            </div>

            {/* Helper text - always visible */}
            <div className="mt-2 px-2">
                <p className="text-xs text-zinc-600">
                    Enter a keyword to discover trending topics
                </p>
            </div>
        </div>
    );
};

export default KeywordInput;
